"use client";

import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Separator } from "@/components/ui/separator";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const testimonials = [{
  author: "Jane D.",
  quote: "NamesOfTheWorld.com helped us find the perfect name for our baby girl. The lists are so easy to browse!",
  position: "Happy Parent"
}, {
  author: "Mark T.",
  quote: "I love the clean design and the variety of names available. Highly recommended!",
  position: "Name Enthusiast"
}];
export default function ParentSuccessStories() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: carouselRef,
    isVisible: isCarouselVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="parent-success-stories" className="py-24 bg-card/30 overflow-hidden">
      <div className="max-w-7xl mx-auto px-8">
        <div ref={headerRef} className={`text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-chart-1 tracking-tight">
            Parent Success Stories
          </h2>
        </div>

        <div ref={carouselRef} className={`max-w-4xl mx-auto px-4 md:px-12 transition-all duration-1000 delay-200 ease-out ${isCarouselVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Carousel opts={{
          align: "center",
          loop: true
        }} className="w-full">
            <CarouselContent>
              {testimonials.map((testimonial, i) => <CarouselItem key={i} data-index={i} className="md:basis-full">
                  <Card className="bg-transparent border-none shadow-none">
                    <CardContent className="flex flex-col items-center text-center space-y-8 p-6 md:p-12">
                      <p className="text-xl md:text-2xl text-foreground italic font-serif leading-relaxed max-w-2xl">
                        "{testimonial.quote}"
                      </p>
                      
                      <Separator className="w-16 h-1 bg-chart-1 rounded-full" />
                      
                      <div className="space-y-1.5">
                        <p className="font-semibold text-foreground text-lg">
                          {testimonial.author}
                        </p>
                        <p className="text-sm text-muted-foreground uppercase tracking-wider font-medium">
                          {testimonial.position}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>)}
            </CarouselContent>
            <div className="hidden md:block">
              <CarouselPrevious className="bg-background hover:bg-card hover:text-chart-1 border-border/50" />
              <CarouselNext className="bg-background hover:bg-card hover:text-chart-1 border-border/50" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}